import { DatePicker, DatePickerProps, InputNumber, Select, Space } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import dayjs, { Dayjs } from 'dayjs';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { nullishValue } from '../../../utils/helpers';
import { customFieldTypeMap, dateTypeCustomFieldMap } from '../../CustomField/CustomField.type';
import { GrispiDateInputProps } from '../../GrispiInput/components/GrispiDateInput';
import { GRISPI_ANNIVERSARY_KEY } from '../../GrispiInput/GrispiInputAnniversaryController';

const { RangePicker } = DatePicker;

const createDayjsObject = (value: number) => dayjs(value);
const isDayjsObject = (value: dayjs.Dayjs) => dayjs.isDayjs(value);

const DATE_TYPE_FIELD_TYPE_LIST = Object.keys(dateTypeCustomFieldMap).map((type) => type);

const valueRequiredDateTimeFieldOperatorTypeMap = {
    OLDER_THAN_DURATION: 'OLDER_THAN_DURATION', // duration in ms i.e, (24*60*60*1000)
    NEWER_THAN_DURATION: 'NEWER_THAN_DURATION', // duration in ms i.e, (24*60*60*1000)
    LESS_THAN_DATE: 'LESS_THAN_DATE', // date in ms i.e, (1677790799999)
    GREATER_THAN_DATE: 'GREATER_THAN_DATE', // date in ms i.e, (1677790799999)
    BETWEEN: 'BETWEEN', // date in ms i.e, "1614452657000:1677790799999"
    BEFORE: 'BEFORE',
    AFTER: 'AFTER',
    EQUAL: 'EQUAL',
    NOT_EQUAL: 'NOT_EQUAL'
} as const;

const VALUE_REQUIRED_DATE_TIME_FIELD_OPERATOR_LIST = Object.keys(valueRequiredDateTimeFieldOperatorTypeMap).map(
    (operator) => operator
);

const TWO_INPUT_REQUIRED_DATE_TIME_FIELD_OPERATOR_LIST = [
    valueRequiredDateTimeFieldOperatorTypeMap.NEWER_THAN_DURATION.toString(),
    valueRequiredDateTimeFieldOperatorTypeMap.OLDER_THAN_DURATION.toString()
];

type ValueRequiredDateTimeFieldOperatorType = keyof typeof valueRequiredDateTimeFieldOperatorTypeMap;

type ConditionDateTimeInputProps = Omit<GrispiDateInputProps, 'containerProps' | 'conditionInput'> & {
    operator: ValueRequiredDateTimeFieldOperatorType;
};

function transformDayjsValue(value: Dayjs, oldValue: string) {
    if (oldValue?.includes(GRISPI_ANNIVERSARY_KEY)) return `${GRISPI_ANNIVERSARY_KEY}${value.valueOf().toString()}`;
    if (isDayjsObject(value)) return value.valueOf().toString();
    return null;
}

function transformFormValue(value: string) {
    if (typeof value === 'string' && value?.includes(GRISPI_ANNIVERSARY_KEY))
        return createDayjsObject(+value?.split(':')[1]);
    if (typeof value === 'string' && !Number.isNaN(+value)) return createDayjsObject(+value);
}

function transformDayjsRangeValue(value: [Dayjs, Dayjs], oldValue: string) {
    if (Array.isArray(value)) {
        const [start, end] = value;
        return `${oldValue?.includes(GRISPI_ANNIVERSARY_KEY) ? GRISPI_ANNIVERSARY_KEY : ''}${start
            .valueOf()
            .toString()}:${end.valueOf().toString()}`;
    }
    return null;
}

function transformFormValueForRangePicker(value: string): [Dayjs, Dayjs] {
    if (typeof value === 'string') {
        if (value?.includes(GRISPI_ANNIVERSARY_KEY)) {
            return [createDayjsObject(+value.split(':')[1]), createDayjsObject(+value.split(':')[2])];
        }
        const [start, end] = value.split(':');
        return [createDayjsObject(+start), createDayjsObject(+end)];
    }
}

const multiplierMap = {
    min: 60 * 1000,
    hour: 60 * 60 * 1000,
    day: 60 * 60 * 1000 * 24,
    week: 60 * 60 * 1000 * 24 * 7,
    month: 60 * 60 * 1000 * 24 * 30,
    year: 60 * 60 * 1000 * 24 * 365
};
function transformFormValueForInputGroup<
    T extends 'value' | 'timeOption' = 'value',
    RT = T extends 'value' ? number : TimeOption
>(value: string, inputType: T): RT {
    if (typeof value === 'string') {
        if (!value?.includes(GRISPI_ANNIVERSARY_KEY)) {
            const [valueInMs, timeOption] = value.split(':');
            if (inputType === 'value')
                return (!Number.isNaN(+valueInMs) ? +valueInMs / multiplierMap[timeOption ?? 'hour'] : null) as RT;
            if (inputType === 'timeOption') return (timeOption ?? 'hour') as RT;
        } else {
            const [, valueInMs, timeOption] = value.split(':');
            if (inputType === 'value')
                return (!Number.isNaN(+valueInMs) ? +valueInMs / multiplierMap[timeOption ?? 'hour'] : null) as RT;
            if (inputType === 'timeOption') return (timeOption ?? 'hour') as RT;
        }
    }
}

function transformInputGroupValue(value: number, timeOption: TimeOption, oldValue: string) {
    const isAnniversary = oldValue?.includes(GRISPI_ANNIVERSARY_KEY);
    if (typeof value === 'number')
        return `${isAnniversary ? GRISPI_ANNIVERSARY_KEY : ''}${value * multiplierMap[timeOption]}:${timeOption}`;
    if (nullishValue(value)) return `${isAnniversary ? GRISPI_ANNIVERSARY_KEY : ''}null:${timeOption}`;
}

const localizedOption = (t: TFunction) => [
    { label: t('conditionOperators.timeOption.min'), value: 'min' },
    { label: t('conditionOperators.timeOption.hour'), value: 'hour' },
    { label: t('conditionOperators.timeOption.day'), value: 'day' },
    { label: t('conditionOperators.timeOption.week'), value: 'week' },
    { label: t('conditionOperators.timeOption.month'), value: 'month' },
    { label: t('conditionOperators.timeOption.year'), value: 'year' }
];

type TimeOption = keyof typeof multiplierMap;

/**
 * @param {value} - milliseconds:timeOptions - 3600000:hour
 */
function InputGroup({ onChange, value, ...rest }: Pick<ConditionDateTimeInputProps, 'onChange' | 'value'>) {
    const { t } = useTranslation('admin');
    const valueToRender = transformFormValueForInputGroup(value, 'value');
    const timeOption = transformFormValueForInputGroup<'timeOption'>(value, 'timeOption') ?? 'hour';

    // 31536000000;
    return (
        <Space.Compact>
            <InputNumber
                min={1}
                inputMode="numeric"
                step={1}
                precision={0}
                value={valueToRender}
                onChange={(v) => onChange(transformInputGroupValue(v, timeOption, value))}
                style={{ width: '100%' }}
                {...rest}
            />

            <Select<string>
                showSearch
                optionFilterProp="label"
                optionLabelProp="label"
                options={localizedOption(t)}
                onSelect={(nextTimeOption: TimeOption) =>
                    onChange(transformInputGroupValue(valueToRender, nextTimeOption, value))
                }
                value={timeOption}
                style={{ width: '100%' }}
                {...rest}
            />
        </Space.Compact>
    );
}
export default function ConditionDateTimeInput({
    antdProps,
    onChange,
    value,
    operator,
    type,
    ...rest
}: ConditionDateTimeInputProps) {
    if (operator === valueRequiredDateTimeFieldOperatorTypeMap.BETWEEN) {
        return (
            <RangePicker
                style={{ width: '100%' }}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                showTime={type === customFieldTypeMap.DATE_TIME && { format: 'HH:mm' }}
                format={type === customFieldTypeMap.DATE_TIME ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY'}
                {...(antdProps as RangePickerProps)}
                allowClear
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                onChange={(v) => onChange(transformDayjsRangeValue(v, value))}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                value={transformFormValueForRangePicker(value)}
                {...rest}
            />
        );
    }

    if (TWO_INPUT_REQUIRED_DATE_TIME_FIELD_OPERATOR_LIST.includes(operator)) {
        return <InputGroup onChange={onChange} value={value} {...rest} />;
    }

    return (
        <DatePicker
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            {...(antdProps as DatePickerProps)}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            onChange={(v) => onChange(transformDayjsValue(v, value))}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            value={transformFormValue(value)}
            {...rest}
        />
    );
}

export {
    DATE_TYPE_FIELD_TYPE_LIST,
    TWO_INPUT_REQUIRED_DATE_TIME_FIELD_OPERATOR_LIST,
    VALUE_REQUIRED_DATE_TIME_FIELD_OPERATOR_LIST,
    valueRequiredDateTimeFieldOperatorTypeMap,
    type ValueRequiredDateTimeFieldOperatorType
};
